import {areas, DataShape, getRecent, getValidValue} from "../App";
import React, {ReactElement} from "react";
import {RtInfection} from "./Rt";

const RtDescription = ({data, message, rtInfection}: { data: DataShape, message: string, rtInfection: RtInfection[] }) => {
    /**
     * Given a growth rate (rt), and a infectious duration (period), calculate
     * how many days it would take to double (if rt > 1) or halve (if rt < 1).
     *
     * @param rt
     * @param period
     */
    const doublingHalvingTime = (rt: number, period: number): number => {
        if (rt === 1) return 0;
        return period * ( Math.log(2) / Math.log(rt) )
    }

    const rtRecent = getRecent(data.metricsTimeseries, (val, acc) => getValidValue(val.infectionRate, acc));

    const today = new Date();
    const thirtyDayCases = data.actualsTimeseries.reduce<number>((acc, val) => {
        const entryDate = new Date(val.date);
        if (entryDate.getTime() + 30*24*60*60*1000 > today.getTime()) {
            if (val.newCases) {
                acc += val.newCases;
            }
        }
        return acc;
    }, 0);
    const thirtyDayDeaths = data.actualsTimeseries.reduce<number>((acc, val) => {
        const entryDate = new Date(val.date);
        if (entryDate.getTime() + 30*24*60*60*1000 > today.getTime()) {
            if (val.newDeaths) {
                acc += val.newDeaths;
            }
        }
        return acc;
    }, 0);

    const casesPerDeath = thirtyDayCases / (thirtyDayDeaths > 0 ? thirtyDayDeaths : 1);
    const exactChain = Math.log(1 - (casesPerDeath * (1 - rtRecent))) / Math.log(rtRecent);
    const chain = Math.ceil(exactChain);
    const deathLow = Math.ceil(exactChain * 5);
    const deathHigh = Math.ceil(exactChain * 16);

    const rtUnvaccinated = rtInfection[0].data.reduce<number>((acc, val) => { return val.rtHigh ? val.rtHigh : acc }, 0);

    const caseProjection = (rt: number): ReactElement => {
        const growthLow = Math.ceil(doublingHalvingTime(rt, 5));
        const growthHigh = Math.ceil(doublingHalvingTime(rt, 16));
        if (rt === 1) { return (
            <>
                hold steady
            </>
        )}
        else {
            return (
                <>
                    {rt < 1 ? "halve" : "double"} every <b>{Math.abs(growthLow)}</b> to <b>{Math.abs(growthHigh)}</b> days
                </>
            )
        }
    }

    return (
        <>
            <p>
                The current Rt for {areas[message]} expects cases
                to {caseProjection(rtRecent)}.  If no one were vaccinated, cases
                would {caseProjection(rtUnvaccinated)}.
            </p>
            {rtRecent > 1 &&
            <p>
                Currently, given the 30-day case rate and death rate in this region, one infection
                will cause a death after <b>{chain}</b> infection generations on average, or
                within <b>{deathLow}</b> to <b>{deathHigh}</b> days if Rt stays steady.
            </p>
            }
        </>
    )
}

export default RtDescription;
